<template>
  <div :class="rootClass">
    <v-toolbar>
      <v-btn-toggle dense>
        <PromptDialog
          v-model="visibleConfirm"
          :visible="false"
          title="Confirm the transfer of structures to AMOS"
          :html-message="true"
          text="Too many structures selected. Only the first 150 structures will be transferred. Continue?"
          @confirmed="transferToAmosConfirm"
        />
        <PromptDialog
          v-model="visibleConfirmChet"
          :visible="false"
          title="Confirm the transfer of structures to CheT"
          :html-message="true"
          text="Too many structures selected. Only the first 150 structures will be transferred. Continue?"
          @confirmed="transferToChetConfirm"
        />
      </v-btn-toggle>
    </v-toolbar>

    <ActivatorButton
      :disabled="disabled"
      :tooltip-text="transferTooltipText"
      :v-btn-props="{ class: buttonClass, id: menuActivatorId }"
    >
      <v-icon v-if="onlyIcon">mdi-transfer-up</v-icon>
      {{ onlyIcon ? '' : 'Transfer' }} {{ showCount && records?.length ? `(${records.length})` : '' }}

      <v-icon v-if="dropdownMode">mdi-menu-up</v-icon>
    </ActivatorButton>

    <v-menu
      :activator="`#${menuActivatorId}`"
      offset-y
    >
      <v-list :class="{ 'dropdown-mode': dropdownMode }">
        <template v-if="fullMode">
          <v-list-item
            v-if="feature('hazard')"
            title="Transfer selected structures to HCD"
            @click="transfer('/hazard', records)"
          >
            Hazard
          </v-list-item>
          <v-list-item
            v-if="feature('safety')"
            title="Transfer selected structures to Safety"
            @click="transfer('/safety', records)"
          >
            Safety
          </v-list-item>
          <v-list-item
            v-if="feature('alerts')"
            title="Transfer selected structures to Alerts"
            @click="transfer('/alerts', records)"
          >
            Alerts
          </v-list-item>
          <v-list-item
            v-if="feature('webtest')"
            title="Transfer selected structures to Predict Batch"
            @click="transfer('/combined-predict', records)"
          >
            Predict
          </v-list-item>
          <v-list-item
            v-if="feature('stdizer')"
            title="Transfer selected structures to Standardizer"
            @click="transfer('/stdizer', records)"
          >
            Standardizer
          </v-list-item>
          <v-list-item
            v-if="feature('toxprints')"
            title="Transfer selected structures to Fingerprints"
            @click="transfer('/toxprints', records)"
          >
            Fingerprints
          </v-list-item>
          <v-list-item
            v-if="feature('resolver')"
            title="Transfer selected structures to Utilities"
            @click="transfer('/utils', records)"
          >
            Utilities
          </v-list-item>
        </template>
        <v-list-item
          v-if="feature('amos')"
          title="Transfer selected structures to AMOS"
          @click="transferToAmos(records)"
        >
          AMOS
        </v-list-item>
        <v-list-item
          v-if="feature('chet')"
          title="Transfer selected structures to CheT"
          @click="transferToChet(records)"
        >
          CheT
        </v-list-item>
      </v-list>
    </v-menu>

    <ActivatorButton
      v-if="!fullMode && !hideAddButton"
      :disabled="disabled"
      @activate="onAdd()"
      :v-btn-props="{ class: buttonClass }"
      :tooltip-text="addToCardTooltipText"
    >
      <v-icon>mdi-cart-plus</v-icon>
      {{ showCount && records?.length ? `(${records.length})` : '' }}
    </ActivatorButton>
  </div>
</template>

<script>
import FeaturesBase from '@/components/FeaturesBase'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import { AMOS_URL, CHET_URL, openLink } from '../main'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import ActivatorButton from '@/components/ActivatorButton.vue'

export default {
  components: {
    PromptDialog,
    ActivatorButton,
  },

  props: {
    records: {
      type: Array,
      required: true,
    },

    // used only to override styles for the menu on the search page
    dropdownMode: {
      type: Boolean,
      default: false,
    },

    showCount: {
      type: Boolean,
      default: false,
    },

    ui: {
      type: Object,
      default: () => ({}),
    },

    fullMode: {
      type: Boolean,
      default: false,
    },

    onlyIcon: {
      type: Boolean,
      default: false,
    },

    hideAddButton: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [FeaturesBase],
  computed: {
    rootClass() {
      return this.ui.root && this.fullMode ? this.ui.root : `gap-1 ${this.ui?.root ?? ''} d-flex flex-row items-center`
    },
    buttonClass() {
      return this.ui?.button ? this.ui.button : 'flat-button border'
    },
    transferTooltipText() {
      return `Transfer selected (${this.records?.length})`
    },

    addToCardTooltipText() {
      return `Add to the cart (${this.records?.length})`
    },

    disabled() {
      return !this.records?.length
    },
  },
  data: () => ({
    DTXSIDS_COUNT_LIMIT: 150,
    visibleConfirm: false,
    dtxsidsToConfirm: [],
    visibleConfirmChet: false,
    idsToConfirmChet: [],
    defaultVTooltipProps,
    menuActivatorId: `transfer-menu-activator-button-${Math.random().toString(36).substr(2, 9)}`,
  }),
  methods: {
    removeErrorFieldFromRecords(records) {
      return records.map((record) => {
        const { error, ...rest } = record
        return rest
      })
    },
    onAdd() {
      this.$store.dispatch('addUniqueStructures', this.removeErrorFieldFromRecords(this.records))
      this.$store.commit('SET_VIEW_STRUCTURES', true)
    },

    transfer(target, records) {
      console.log('all records', this.records)
      this.$store.commit('CLEAR_STRUCTURES')
      this.$store.dispatch('addUniqueStructures', records).then(() => this.$router.push(target))
    },
    transferToAmos(records) {
      const dtxsidsAll = records.map((record) => record.sid).filter((sid) => sid !== undefined && sid !== '')
      const dtxsids = dtxsidsAll.slice(0, this.DTXSIDS_COUNT_LIMIT)
      if (dtxsids.length > 1) {
        if (dtxsidsAll.length > this.DTXSIDS_COUNT_LIMIT) {
          this.visibleConfirm = true
          this.dtxsidsToConfirm = dtxsids
        } else {
          this.transferFromAmosDtxsids(dtxsids)
        }
      } else {
        openLink(AMOS_URL + '/search/' + dtxsids[0], 'amos')
      }
    },
    transferFromAmosDtxsids(dtxsids) {
      openLink(AMOS_URL + '/partial_identifier_search?dtxsids=' + dtxsids.join(','), 'amos')
    },
    transferFromChetIds(ids) {
      openLink(CHET_URL + '/reaction/batchsearch?ids=' + ids.join(','), 'chet')
    },
    transferToAmosConfirm() {
      this.transferFromAmosDtxsids(this.dtxsidsToConfirm)
    },
    transferToChetConfirm() {
      this.transferFromChetIds(this.idsToConfirmChet)
    },

    transferToChet(records) {
      const idsAll = records
        .map((record) => (record.sid != null ? record.sid : record.casrn != null ? record.casrn : record.inchiKey))
        .filter((id) => id !== undefined && id !== '')
      const ids = idsAll.slice(0, this.DTXSIDS_COUNT_LIMIT)
      if (idsAll.length > this.DTXSIDS_COUNT_LIMIT) {
        this.visibleConfirmChet = true
        this.idsToConfirmChet = ids
      } else {
        this.transferFromChetIds(ids)
      }
    },
  },
}
</script>

<style scoped>
.v-toolbar {
  display: none;
}

/* dropdown mode */
</style>
